import React from 'react'
import { css } from '@emotion/css'
import { CellRenderer, CellProps } from '../../../legacy-design-system/grid'
import { VulnerabilityApplication } from '../shared'
import { LoadingTextMask } from '../../../legacy-design-system/loading-masks'
import { ApplicationTooltip } from '../application-tooltip'
import { AgentFeature } from '../../agent-features'
import { FormattedMessage } from 'react-intl'
import { readonlyTextCss } from './status'
import { Role } from '@contrast-security-inc/base-types'

const cellCss = css`
  padding: 8px;
  width: 100%;
`

interface GetRendererProps {
  organizationID: string
  organizationRole: Role
  hasLicenses: boolean
  features: AgentFeature[]
  renderLink?: boolean
}

interface Vulnerability {
  application: VulnerabilityApplication
}

interface SinkGroup {
  application?: VulnerabilityApplication
}

interface RowData {
  sinkGroup?: SinkGroup
  vulnerability?: Vulnerability
}

export function getApplicationCellRenderer({
  organizationID,
  organizationRole,
  hasLicenses,
  features,
  renderLink = true
}: GetRendererProps): CellRenderer<RowData> {
  return ({ rowData, getCellContentProps }) => {
    const { vulnerability, sinkGroup } = rowData

    if (vulnerability) {
      return renderApplicationCell({
        organizationID,
        organizationRole,
        hasLicenses,
        features,
        rowData: vulnerability,
        getCellContentProps,
        renderLink
      })
    } else if (sinkGroup) {
      // eslint-disable-next-line unicorn/prefer-ternary
      if (sinkGroup.application) {
        return renderApplicationCell({
          organizationID,
          organizationRole,
          hasLicenses,
          features,
          rowData: sinkGroup,
          getCellContentProps,
          renderLink
        })
      } else {
        return renderMultipleApplicationCell({ getCellContentProps })
      }
    }
    throw new Error(
      `Invalid application row. Row data should contain a vulnerability or sink group: ${rowData}`
    )
  }
}

function renderMultipleApplicationCell({ getCellContentProps }) {
  return (
    <div {...getCellContentProps({ className: readonlyTextCss })}>
      <FormattedMessage id="title_vulnSeverityTypeMultiple" />
    </div>
  )
}

function renderApplicationCell({
  organizationID,
  organizationRole,
  hasLicenses,
  features,
  rowData: { application },
  getCellContentProps,
  renderLink
}: GetRendererProps & CellProps<Vulnerability | SinkGroup>) {
  return (
    <div {...getCellContentProps({ className: cellCss })}>
      {application && (
        <ApplicationTooltip
          organizationID={organizationID}
          organizationRole={organizationRole}
          hasLicenses={hasLicenses}
          features={features}
          application={application}
          clickable={renderLink}
        />
      )}
    </div>
  )
}
export function loadingApplicationCellRenderer() {
  return <LoadingTextMask min={6} max={7} />
}
